import { registerJdid as registerJdidToRum } from '@iheartjane/tracking';
import { useEffect } from 'react';

import { registerJdid as registerJdidToMixpanel } from '@jane/bloom/services/tracking/mixpanel';
import {
  getCookieFromDocument,
  setCookieForRootDomain,
} from '@jane/bloom/utils/cookie.client';
import {
  JDID_KEY,
  TWO_YEARS_IN_SECONDS,
  isValidJdid,
} from '@jane/bloom/utils/jdid';
import { logError } from '@jane/bloom/utils/logError';

/**
 * Ensures the server JDID is persisted in the root domain cookie.
 *
 * This is called on client hydration to ensure the JDID cookie is set.
 * The cookie is set on the root domain so it's accessible to both the
 * embedded menu row component and the Bloom menu when they're on the
 * same domain or subdomains of the same root domain.
 */
function ensureJdidCookie(serverJdid: string): void {
  if (typeof window === 'undefined') return;
  if (!isValidJdid(serverJdid)) return;

  try {
    const currentCookieJdid = getCookieFromDocument(JDID_KEY);

    if (currentCookieJdid !== serverJdid) {
      setCookieForRootDomain(JDID_KEY, serverJdid, TWO_YEARS_IN_SECONDS);
    }
  } catch (error) {
    logError(error, { context: 'jdid-cookie-sync' });
  }
}

/**
 * Hook that ensures the JDID cookie is set and registers it with tracking services.
 *
 * Handles:
 * - Ensuring the server JDID is persisted in the root domain cookie
 * - Registering JDID with Mixpanel and Datadog RUM
 * @param serverJdid - The JDID provided by the server (may be from cookie or newly generated)
 */
export function useJdidSync(serverJdid: string): void {
  useEffect(() => {
    ensureJdidCookie(serverJdid);
    registerJdidToMixpanel(serverJdid);
    registerJdidToRum(serverJdid);
  }, [serverJdid]);
}
